import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { JSONContent } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";

import Editor from "../../../components/ui/Editor";
import {
  ARTICLE_LANGS,
  ArticleEditLang,
  ArticleLangContent,
  emptyLangContent,
  LANG_LABELS,
  langIsReadyToSave,
  spanishFingerprint,
  TRANSLATABLE_LANGS,
  TranslatableLang,
  translateFromSpanish,
} from "../helpers/articleLang";

type LangOrigin =
  | "original"
  | "translating"
  | "automatic"
  | "stored"
  | "edited"
  | "error";

type Drafts = Record<ArticleEditLang, ArticleLangContent>;
type Origins = Record<ArticleEditLang, LangOrigin>;

type SessionCache = {
  fingerprint: string;
  drafts: Drafts;
  origins: Origins;
};

const ORIGIN_CHIP: Record<
  LangOrigin,
  { label: string; color: "default" | "info" | "success" | "warning" | "error" }
> = {
  original: { label: "Original", color: "default" },
  translating: { label: "Traduciendo", color: "default" },
  automatic: { label: "Traducción automática", color: "info" },
  stored: { label: "Ya guardada", color: "success" },
  edited: { label: "Editada a mano", color: "warning" },
  error: { label: "Sin traducción", color: "error" },
};

interface AdminArticleTranslationsDialogProps {
  open: boolean;
  isEdit: boolean;
  spanish: ArticleLangContent;
  storedTranslations: Record<TranslatableLang, ArticleLangContent>;
  categoryName: string;
  saving: boolean;
  onClose: () => void;
  onConfirm: (drafts: Drafts) => Promise<void>;
}

const cloneDrafts = (drafts: Drafts): Drafts => ({
  es: { ...drafts.es, content: drafts.es.content },
  en: { ...drafts.en, content: drafts.en.content },
  pt: { ...drafts.pt, content: drafts.pt.content },
});

export default function AdminArticleTranslationsDialog({
  open,
  isEdit,
  spanish,
  storedTranslations,
  categoryName,
  saving,
  onClose,
  onConfirm,
}: AdminArticleTranslationsDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const sessionRef = useRef<SessionCache | null>(null);
  const runIdByLang = useRef<Record<TranslatableLang, number>>({ en: 0, pt: 0 });
  const draftsRef = useRef<Drafts>({
    es: emptyLangContent(),
    en: emptyLangContent(),
    pt: emptyLangContent(),
  });
  const originsRef = useRef<Origins>({
    es: "original",
    en: "translating",
    pt: "translating",
  });
  const ignoreEditorChange = useRef<Record<ArticleEditLang, boolean>>({
    es: false,
    en: false,
    pt: false,
  });

  const [activeLang, setActiveLang] = useState<ArticleEditLang>("es");
  const [drafts, setDrafts] = useState<Drafts>(draftsRef.current);
  const [origins, setOrigins] = useState<Origins>(originsRef.current);
  const [editorKeys, setEditorKeys] = useState<Record<ArticleEditLang, number>>({
    es: 0,
    en: 0,
    pt: 0,
  });
  const [errorByLang, setErrorByLang] = useState<Partial<Record<TranslatableLang, string>>>(
    {},
  );
  const [confirmAttempted, setConfirmAttempted] = useState(false);

  const commit = useCallback((nextDrafts: Drafts, nextOrigins: Origins) => {
    draftsRef.current = nextDrafts;
    originsRef.current = nextOrigins;
    setDrafts(nextDrafts);
    setOrigins(nextOrigins);
    sessionRef.current = {
      fingerprint: spanishFingerprint(nextDrafts.es),
      drafts: cloneDrafts(nextDrafts),
      origins: { ...nextOrigins },
    };
  }, []);

  const applyLangPatch = (
    lang: ArticleEditLang,
    patch: Partial<ArticleLangContent>,
    origin?: LangOrigin,
  ) => {
    const nextDrafts = {
      ...draftsRef.current,
      [lang]: { ...draftsRef.current[lang], ...patch },
    };
    const nextOrigins =
      origin
        ? { ...originsRef.current, [lang]: origin }
        : lang !== "es" && originsRef.current[lang] !== "translating"
          ? { ...originsRef.current, [lang]: "edited" as LangOrigin }
          : originsRef.current;
    commit(nextDrafts, nextOrigins);
  };

  const bumpEditor = (lang: ArticleEditLang) => {
    ignoreEditorChange.current[lang] = true;
    setEditorKeys((prev) => ({ ...prev, [lang]: prev[lang] + 1 }));
  };

  const translateLang = async (
    source: ArticleLangContent,
    lang: TranslatableLang,
  ) => {
    const runId = ++runIdByLang.current[lang];
    commit(draftsRef.current, { ...originsRef.current, [lang]: "translating" });
    setErrorByLang((prev) => ({ ...prev, [lang]: undefined }));
    try {
      const translated = await translateFromSpanish(source, lang, categoryName);
      if (runIdByLang.current[lang] !== runId) return;
      commit(
        { ...draftsRef.current, [lang]: translated },
        { ...originsRef.current, [lang]: "automatic" },
      );
      bumpEditor(lang);
    } catch (err: any) {
      if (runIdByLang.current[lang] !== runId) return;
      commit(draftsRef.current, { ...originsRef.current, [lang]: "error" });
      setErrorByLang((prev) => ({
        ...prev,
        [lang]:
          err?.message && err.message !== "Network Error"
            ? err.message
            : `No se pudo traducir al ${LANG_LABELS[lang].toLowerCase()}.`,
      }));
    }
  };

  useEffect(() => {
    if (!open) {
      setConfirmAttempted(false);
      return;
    }

    const fingerprint = spanishFingerprint(spanish);
    const cached = sessionRef.current;
    let pending: TranslatableLang[] = [];
    let sourceSpanish = spanish;

    const prepareEditors = () => {
      setActiveLang("es");
      ARTICLE_LANGS.forEach((lang) => {
        ignoreEditorChange.current[lang] = true;
      });
      setEditorKeys((prev) => ({
        es: prev.es + 1,
        en: prev.en + 1,
        pt: prev.pt + 1,
      }));
    };

    if (cached && cached.fingerprint === fingerprint) {
      const nextDrafts = cloneDrafts(cached.drafts);
      const nextOrigins = { ...cached.origins };
      sourceSpanish = nextDrafts.es;

      TRANSLATABLE_LANGS.forEach((lang) => {
        if (cached.origins[lang] === "edited" || langIsReadyToSave(cached.drafts[lang])) {
          return;
        }
        nextOrigins[lang] = "translating";
        pending.push(lang);
      });

      commit(nextDrafts, nextOrigins);
      prepareEditors();
    } else {
      const nextDrafts: Drafts = {
        es: { ...spanish },
        en: emptyLangContent(),
        pt: emptyLangContent(),
      };
      const nextOrigins: Origins = {
        es: "original",
        en: "translating",
        pt: "translating",
      };

      TRANSLATABLE_LANGS.forEach((lang) => {
        const editedInSession =
          cached?.origins[lang] === "edited" ? cached.drafts[lang] : null;
        if (editedInSession) {
          nextDrafts[lang] = editedInSession;
          nextOrigins[lang] = "edited";
          return;
        }

        if (langIsReadyToSave(storedTranslations[lang])) {
          nextDrafts[lang] = storedTranslations[lang];
          nextOrigins[lang] = "stored";
          return;
        }

        pending.push(lang);
      });

      commit(nextDrafts, nextOrigins);
      prepareEditors();
    }

    pending.forEach((lang) => {
      void translateLang(sourceSpanish, lang);
    });

    const runIds = runIdByLang.current;
    return () => {
      runIds.en += 1;
      runIds.pt += 1;
    };
    // Solo al abrir: el español se toma de esa instantánea.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isTranslating = TRANSLATABLE_LANGS.some(
    (lang) => origins[lang] === "translating",
  );

  const handleFieldChange = (
    lang: ArticleEditLang,
    field: "title" | "resumen",
    value: string,
  ) => {
    if (saving || originsRef.current[lang] === "translating") return;
    applyLangPatch(lang, { [field]: value });
  };

  const handleContentChange = (lang: ArticleEditLang, content: JSONContent) => {
    if (saving || originsRef.current[lang] === "translating") return;
    if (ignoreEditorChange.current[lang]) {
      ignoreEditorChange.current[lang] = false;
      return;
    }
    applyLangPatch(lang, { content });
  };

  const handleRegenerate = (lang: TranslatableLang) => {
    if (saving) return;
    void translateLang(draftsRef.current.es, lang);
  };

  const handleConfirm = async () => {
    setConfirmAttempted(true);
    if (isTranslating) return;
    const current = draftsRef.current;
    const missing = ARTICLE_LANGS.filter((lang) => !langIsReadyToSave(current[lang]));
    if (missing.length > 0) {
      setActiveLang(missing[0]);
      return;
    }
    await onConfirm(current);
  };

  const active = drafts[activeLang];
  const activeError =
    activeLang !== "es" ? errorByLang[activeLang as TranslatableLang] : undefined;
  const missingLangs = ARTICLE_LANGS.filter((lang) => !langIsReadyToSave(drafts[lang]));

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      scroll="paper"
      disableEnforceFocus
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: fullScreen ? 0 : "12px",
          height: { xs: "100%", md: "90vh" },
          maxHeight: { md: "90vh" },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pr: 3 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 700, display: "block" }}>
          {isEdit
            ? "Se va a guardar en los tres idiomas"
            : "Se va a crear en los tres idiomas"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 400 }}>
          El artículo queda en español, inglés y portugués. Abrí cada versión para
          revisarla. Si la modificás a mano, se guarda esa edición y no se pisa con la
          traducción automática.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column" }}>
        <Tabs
          value={activeLang}
          onChange={(_e, value: ArticleEditLang) => setActiveLang(value)}
          variant="fullWidth"
          sx={{
            mb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            minHeight: 52,
          }}
        >
          {ARTICLE_LANGS.map((lang) => (
            <Tab
              key={lang}
              value={lang}
              disabled={saving}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {origins[lang] === "translating" && (
                    <CircularProgress size={14} />
                  )}
                  <span>{LANG_LABELS[lang]}</span>
                </Box>
              }
              sx={{ textTransform: "none", fontWeight: 600, minHeight: 52 }}
            />
          ))}
        </Tabs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Chip
            size="small"
            label={ORIGIN_CHIP[origins[activeLang]].label}
            color={ORIGIN_CHIP[origins[activeLang]].color}
          />
          {activeLang !== "es" && (
            <Button
              size="small"
              variant="text"
              onClick={() => handleRegenerate(activeLang as TranslatableLang)}
              disabled={saving || origins[activeLang] === "translating"}
              sx={{ textTransform: "none" }}
            >
              Volver a traducir desde el español
            </Button>
          )}
        </Box>

        {activeLang === "es" && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta es la versión original. Los otros idiomas se generan a partir de ella.
          </Alert>
        )}

        {origins[activeLang] === "edited" && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta versión fue ajustada a mano. Se va a guardar tal como está.
          </Alert>
        )}

        {origins[activeLang] === "stored" && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Se está respetando la traducción ya guardada. Si el español cambió, podés
            regenerarla.
          </Alert>
        )}

        {activeError && origins[activeLang] === "error" && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => handleRegenerate(activeLang as TranslatableLang)}
              >
                Reintentar
              </Button>
            }
          >
            {activeError} Completala a mano o reintentá la traducción.
          </Alert>
        )}

        {origins[activeLang] === "translating" ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              py: 8,
            }}
          >
            <CircularProgress />
            <Typography color="text.secondary">
              Traduciendo al {LANG_LABELS[activeLang].toLowerCase()}...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pb: 1 }}>
            <TextField
              label={`Título en ${LANG_LABELS[activeLang]}`}
              value={active.title}
              onChange={(e) => handleFieldChange(activeLang, "title", e.target.value)}
              error={confirmAttempted && !langIsReadyToSave(active)}
              helperText={
                confirmAttempted && !langIsReadyToSave(active)
                  ? "El título es obligatorio (mínimo 2 caracteres)."
                  : " "
              }
              disabled={saving}
              fullWidth
            />
            <TextField
              label={`Resumen en ${LANG_LABELS[activeLang]}`}
              value={active.resumen}
              onChange={(e) => handleFieldChange(activeLang, "resumen", e.target.value)}
              disabled={saving}
              multiline
              maxRows={5}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Contenido en {LANG_LABELS[activeLang]}
              </Typography>
              <Editor
                key={`${activeLang}-${editorKeys[activeLang]}`}
                content={active.content}
                onChange={(json) => handleContentChange(activeLang, json)}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
          {isTranslating
            ? "Esperá a que terminen las traducciones automáticas."
            : confirmAttempted && missingLangs.length > 0
              ? `Falta el título en ${missingLangs.map((lang) => LANG_LABELS[lang]).join(", ")}.`
              : "Las tres versiones se guardan juntas."}
        </Typography>
        <Button
          onClick={onClose}
          disabled={saving}
          color="inherit"
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          Volver al formulario
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          disabled={saving || isTranslating}
          variant="contained"
          sx={{ textTransform: "none", borderRadius: "8px", minWidth: 160 }}
        >
          {saving ? (
            <CircularProgress size={22} color="inherit" />
          ) : isEdit ? (
            "Guardar los 3 idiomas"
          ) : (
            "Crear en 3 idiomas"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
